import * as DigRender from "../dig/render.ts";
import * as Routine from "../dig/routine.ts";
import * as Dig from "../game/dig.ts";
import type * as Session from "./session.ts";

// The dig as this screen sees it: the ground and everyone on it, this screen's player in full and
// the others as outlines. The view holds on the surface until the dig has begun and you are in
// it; from then on it follows you, with you in the middle.

// A swing is shown from its blow: the room lands it the moment the key reaches it, so the wind-up
// is already over by the time any screen hears of it.
const BLOW = 3;
const FOLLOW_THROUGH = 4;

const breath = (action: "wait" | "ready", now: number): Routine.Pose => ({
  action,
  frame: Math.floor(now / Routine.BREATH_MS) % 2,
});

// Where a swing is in its quarter second, and a breath in its rise and fall, is a matter of server
// time, so before the first pong nobody moves.
const diggerOf = (
  player: Dig.Player,
  now: number,
  synced: boolean,
  ghost: boolean,
): Routine.Digger => {
  const at = { slot: player.slot, column: player.column, row: player.row, ghost };
  const since = now - player.swing.at;
  if (!synced || !player.ready || since >= Dig.SWING_MS) {
    return { ...at, pose: breath(player.ready ? "ready" : "wait", synced ? now : 0) };
  }
  const { swing } = player;
  return {
    ...at,
    pose: {
      action: "dig",
      direction: swing.direction,
      frame: since < Dig.SWING_MS / 2 ? BLOW : FOLLOW_THROUGH,
    },
    target: { column: swing.column, row: swing.row, direction: swing.direction },
  };
};

export const sceneOf = (view: Session.View, clientNow: number): Routine.Scene => {
  const { world } = view;
  const now = clientNow + view.offset;
  const synced = view.samples > 0;
  const you = world.players.find((player) => player.slot === view.you);
  const camera =
    you !== undefined && you.ready && world.started ? Math.max(0, you.row - Routine.CENTER) : 0;
  const originColumn = (you?.column ?? 0) - Routine.COLUMNS / 2;
  // The ground grows with every blow; only what is in view is looked through for each square.
  const near = world.ground.filter(
    (wound) =>
      wound.column >= originColumn &&
      wound.column < originColumn + Routine.COLUMNS &&
      wound.row >= camera &&
      wound.row < camera + Routine.VIEW_ROWS,
  );
  const grid = [...Array(Routine.VIEW_ROWS).keys()].map((row) =>
    [...Array(Routine.COLUMNS).keys()].map((column) =>
      Dig.cellAt(near, originColumn + column, camera + row),
    ),
  );
  const diggers = world.players.map((player) =>
    diggerOf(player, now, synced, player.slot !== view.you),
  );
  const hits: ReadonlyArray<Routine.Hit> = synced
    ? world.players.flatMap((player) => {
        const since = now - player.swing.at;
        if (!player.ready || since < 0 || since >= DigRender.DEBRIS_MS) {
          return [];
        }
        const { column, row, at, direction } = player.swing;
        return [{ column, row, at, direction }];
      })
    : [];
  return { now, camera, originRow: camera, originColumn, grid, diggers, hits };
};
